import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { signaturesAPI } from '../../lib/api';
import { ShieldCheck, Loader2, CheckCircle2, Clock, ExternalLink, Copy, RefreshCw, XCircle, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString('de-DE'); } catch { return iso; } };
const emptySigner = (name = '') => {
  const parts = (name || '').trim().split(' ');
  const last = parts.length > 1 ? parts.slice(1).join(' ') : '';
  return { first_name: parts[0] || '', last_name: last, email: '', mobile_number: '', language: 'de' };
};

export const QesSignatureDialog = ({ open, onOpenChange, doc, employeeName = '', existing = null, configured = true, onChanged }) => {
  const [qes, setQes] = useState(existing);
  const [busy, setBusy] = useState(false);
  const [signers, setSigners] = useState([emptySigner(employeeName)]);

  useEffect(() => {
    if (open) { setQes(existing); setSigners([emptySigner(employeeName)]); }
  }, [open, existing, employeeName]);

  const setSigner = (i, patch) => setSigners((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const addSigner = () => setSigners((s) => [...s, emptySigner()]);
  const removeSigner = (i) => setSigners((s) => s.filter((_, idx) => idx !== i));

  const refresh = async () => {
    if (!qes) return;
    setBusy(true);
    try { const { data } = await signaturesAPI.getQes(qes.id); setQes(data); onChanged?.(); if (data.status === 'signed') toast.success('QES abgeschlossen'); }
    catch { toast.error('Status konnte nicht aktualisiert werden'); }
    finally { setBusy(false); }
  };

  const create = async () => {
    for (const s of signers) {
      if (!s.first_name.trim() || !s.last_name.trim()) { toast.error('Bitte Vor- und Nachname jedes Unterzeichners angeben'); return; }
      if (!/.+@.+\..+/.test(s.email)) { toast.error('Bitte gültige E-Mail angeben'); return; }
      if (!/^\+[1-9][0-9]{7,14}$/.test(s.mobile_number)) { toast.error(`Mobilnummer für ${s.first_name} muss im Format +49170... sein`); return; }
    }
    setBusy(true);
    try {
      const { data } = await signaturesAPI.createQes(doc.id, { base_url: window.location.origin, signers });
      setQes(data); onChanged?.();
      toast.success('QES-Anfrage an Skribble gesendet');
    } catch (e) { toast.error(e?.response?.data?.detail || 'QES-Anfrage fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const withdraw = async () => {
    if (!window.confirm('QES-Anfrage bei Skribble zurückziehen?')) return;
    setBusy(true);
    try { await signaturesAPI.withdrawQes(qes.id); toast.success('Zurückgezogen'); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Zurückziehen fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const copy = (url) => navigator.clipboard.writeText(url).then(() => toast.success('Signier-Link kopiert')).catch(() => window.prompt('Link:', url));

  const statusBadge = (st) => {
    const map = {
      signed: ['bg-emerald-100 text-emerald-700 border-emerald-200', CheckCircle2, 'Signiert'],
      pending: ['bg-amber-100 text-amber-700 border-amber-200', Clock, 'Ausstehend'],
      declined: ['bg-red-100 text-red-700 border-red-200', XCircle, 'Abgelehnt'],
      withdrawn: ['bg-muted text-muted-foreground', XCircle, 'Zurückgezogen'],
      expired: ['bg-muted text-muted-foreground', Clock, 'Abgelaufen'],
      error: ['bg-red-100 text-red-700 border-red-200', AlertTriangle, 'Fehler'],
    };
    const [cls, Icon, label] = map[st] || map.pending;
    return <Badge variant="outline" className={cls} data-testid="qes-status"><Icon className="h-3.5 w-3.5 mr-1" /> {label}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="qes-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> QES – Qualifizierte Signatur (Skribble)</DialogTitle>
          <DialogDescription className="truncate">{doc?.title} · eIDAS, rechtsverbindlich</DialogDescription>
        </DialogHeader>

        {!configured ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm text-amber-800 dark:text-amber-300" data-testid="qes-not-configured">
            <p className="font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Skribble ist noch nicht konfiguriert</p>
            <p className="mt-1">Bitte im Backend die API-Zugangsdaten (SKRIBBLE_API_USERNAME / SKRIBBLE_API_KEY) hinterlegen, um QES nutzen zu können.</p>
          </div>
        ) : !qes ? (
          // ---- Setup ----
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Jede unterzeichnende Person identifiziert sich bei Skribble (Video-Ident/eID) und signiert dort. Namen müssen mit dem Ausweisdokument übereinstimmen.</p>
            {signers.map((s, i) => (
              <div key={i} className="rounded-lg border border-border/60 p-3 space-y-3" data-testid={`qes-signer-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium flex items-center gap-2"><Badge variant="outline">{i + 1}</Badge> Unterzeichner</span>
                  {signers.length > 1 && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeSigner(i)} data-testid={`qes-remove-signer-${i}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Vorname *</Label><Input value={s.first_name} onChange={(e) => setSigner(i, { first_name: e.target.value })} className="h-8" data-testid={`qes-first-${i}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Nachname *</Label><Input value={s.last_name} onChange={(e) => setSigner(i, { last_name: e.target.value })} className="h-8" data-testid={`qes-last-${i}`} /></div>
                </div>
                <div className="space-y-1"><Label className="text-xs">E-Mail *</Label><Input type="email" value={s.email} onChange={(e) => setSigner(i, { email: e.target.value })} className="h-8" placeholder="person@example.de" data-testid={`qes-email-${i}`} /></div>
                <div className="space-y-1"><Label className="text-xs">Mobilnummer * (für Identifizierung, +49…)</Label><Input value={s.mobile_number} onChange={(e) => setSigner(i, { mobile_number: e.target.value })} className="h-8" placeholder="+491701234567" data-testid={`qes-mobile-${i}`} /></div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addSigner} data-testid="qes-add-signer"><Plus className="h-4 w-4 mr-1" /> Weiteren Unterzeichner</Button>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
              <Button onClick={create} disabled={busy} data-testid="qes-create-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} QES anfordern</Button>
            </DialogFooter>
          </div>
        ) : (
          // ---- Status ----
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              {statusBadge(qes.status)}
              <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="qes-refresh-btn"><RefreshCw className={`h-4 w-4 mr-1 ${busy ? 'animate-spin' : ''}`} /> Aktualisieren</Button>
            </div>
            <div className="space-y-2">
              {qes.signers.map((s, i) => (
                <div key={i} className="rounded-lg border border-border/60 p-3" data-testid={`qes-signer-row-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.first_name} {s.last_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{s.email} · {s.mobile_number}</p>
                    </div>
                    {s.signed_status && <Badge variant="outline" className="shrink-0 text-[10px]">{s.signed_status}</Badge>}
                  </div>
                  {qes.status === 'pending' && s.signing_url && (
                    <div className="mt-2 flex gap-2">
                      <a href={s.signing_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`qes-open-${i}`}><ExternalLink className="h-4 w-4 mr-1" /> Signieren öffnen</Button></a>
                      <Button size="sm" variant="outline" onClick={() => copy(s.signing_url)} data-testid={`qes-copy-${i}`}><Copy className="h-4 w-4 mr-1" /> Link kopieren</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {qes.status === 'signed' && <p className="text-sm text-emerald-700 dark:text-emerald-400">QES abgeschlossen. Das rechtsverbindliche PDF liegt in der Akte unter „Signierte Dokumente".</p>}
            {qes.status === 'pending' && <p className="text-xs text-muted-foreground">Die Unterzeichner erhalten den Signier-Link auch per E-Mail von Skribble. Status hier über „Aktualisieren" prüfen.</p>}
            <DialogFooter className="gap-2 sm:justify-between">
              {qes.status === 'pending'
                ? <Button variant="ghost" className="text-destructive" onClick={withdraw} disabled={busy} data-testid="qes-withdraw-btn"><XCircle className="h-4 w-4 mr-1" /> Zurückziehen</Button>
                : <span />}
              <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
